import { parseNumbers } from "./numbers";
import type {
  RecognitionResult,
  RecognizerStatus,
  SpeechRecognizer,
  SpeechRecognizerEvents,
} from "./types";

// Запасной распознаватель на браузерном Web Speech API (webkitSpeechRecognition).
// Не основной путь: включается только из панели фасилитатора, если нужно
// сравнить задержку/точность с ElevenLabs STT или если сервер недоступен.
// Тот же интерфейс SpeechRecognizer — игровая логика не меняется.

export class WebSpeechRecognizer implements SpeechRecognizer {
  readonly name = "webspeech";
  status: RecognizerStatus = "idle";

  private ev: SpeechRecognizerEvents;
  private rec: any = null;
  private muted = false;
  private running = false;
  private startTs = 0;

  constructor(events: SpeechRecognizerEvents) {
    this.ev = events;
  }

  static get supported(): boolean {
    return !!((window as any).webkitSpeechRecognition || (window as any).SpeechRecognition);
  }

  private setStatus(s: RecognizerStatus) {
    this.status = s;
    this.ev.onStatus(s);
  }

  async start(): Promise<void> {
    const Impl = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (!Impl) throw new Error("Web Speech API not supported");
    this.rec = new Impl();
    this.rec.lang = "ru-RU";
    this.rec.continuous = true;
    this.rec.interimResults = true;
    this.rec.maxAlternatives = 1;

    let spoke = false;
    this.rec.onspeechstart = () => {
      spoke = true;
      this.startTs = Date.now();
      this.setStatus("capturing");
      this.ev.onSpeechStart(this.startTs);
    };
    this.rec.onresult = (e: any) => {
      if (this.muted) return;
      const res = e.results[e.results.length - 1];
      const text = (res[0]?.transcript || "").trim();
      const conf = res[0]?.confidence;
      const numbers = parseNumbers(text);
      if (!res.isFinal && numbers.length === 0) return;
      if (res.isFinal) this.ev.onSpeechEnd(Date.now());
      const result: RecognitionResult = {
        numbers,
        rawText: text,
        confidence: conf,
        startedAt: this.startTs || Date.now(),
        endedAt: Date.now(),
        recognizedAt: Date.now(),
      };
      this.ev.onResult(result);
    };
    this.rec.onerror = (e: any) => {
      if (e.error === "no-speech" || e.error === "aborted") return;
      this.ev.onError(`webspeech: ${e.error}`);
    };
    this.rec.onend = () => {
      // iOS обрывает непрерывный режим после паузы — перезапускаем.
      if (this.running) {
        try {
          this.rec.start();
        } catch {
          /* уже запущен */
        }
      }
    };

    this.running = true;
    this.rec.start();
    this.setStatus("listening");
    void spoke;
  }

  stop(): void {
    this.running = false;
    try {
      this.rec?.stop();
    } catch {
      /* noop */
    }
    this.rec = null;
    this.setStatus("idle");
  }

  mute(): void {
    this.muted = true;
    this.setStatus("muted");
  }

  unmute(): void {
    this.muted = false;
    if (this.running) this.setStatus("listening");
  }
}
